import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ARTICLES } from "../data/articles";
import { BOOKING_URL } from "../data/site";
import { JournalCard } from "./Blog";
import { useJournalPost } from "../lib/useJournal";
import { ArticleBookCard } from "../components/ArticleBookCard";
import { ShareButtons } from "../components/ShareButtons";
import { SkinTipsSignup } from "../components/SkinTipsSignup";

const ext = (href = "") => href.startsWith("http");
// Links to posts on the old Wix blog now point to the same article in The Journal.
const local = (href = "") => href.replace(/^https?:\/\/(www\.)?luxveritae\.com\/post\//, "/blog/");
const MD = {
  a: ({ href, children }) => { const h = local(href); return <a href={h} target={ext(h) ? "_blank" : undefined} rel="noreferrer" className="underline decoration-neutral-400 underline-offset-4 hover:decoration-neutral-950">{children}</a>; },
  img: ({ src, alt }) => <img src={src} alt={alt} className="my-12 w-full" loading="lazy" />,
  h2: ({ children }) => <h2 className="font-vogue mb-6 mt-16 text-3xl leading-tight text-neutral-950 md:text-4xl">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-3 mt-10 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-950">{children}</h3>,
  blockquote: ({ children }) => <blockquote className="font-display my-14 border-y border-neutral-950 py-10 text-center text-2xl italic leading-snug text-neutral-900 md:text-3xl [&_p]:m-0">{children}</blockquote>,
  table: ({ children }) => <div className="my-10 overflow-x-auto"><table className="w-full border-t border-neutral-950 text-left text-sm">{children}</table></div>,
  th: ({ children }) => <th className="border-b border-neutral-300 py-3 pr-4 text-[10px] font-semibold uppercase tracking-[0.2em]">{children}</th>,
  td: ({ children }) => <td className="border-b border-neutral-200 py-3 pr-4 align-top font-light">{children}</td>,
  ul: ({ children }) => <ul className="my-6 list-disc space-y-2 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-6 list-decimal space-y-2 pl-5">{children}</ol>,
};

// Splits the article at the H2 closest to its middle, so the booking card sits inside the story.
const splitBody = (body = "") => {
  const cuts = [...body.matchAll(/\n## /g)].map((m) => m.index);
  if (cuts.length < 2) return [body, ""];
  const cut = cuts.reduce((best, c) => (Math.abs(c - body.length / 2) < Math.abs(best - body.length / 2) ? c : best));
  return [body.slice(0, cut), body.slice(cut)];
};

export default function Article() {
  const { slug } = useParams();
  const local = ARTICLES.find((x) => x.slug === slug);
  const { post, done } = useJournalPost(slug, Boolean(local));
  const a = local || post;
  useEffect(() => {
    if (!a) return undefined;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting", headline: a.title, description: a.dek,
      image: a.image ? new URL(a.image, window.location.origin).href : undefined,
      author: { "@type": "Person", name: a.author }, publisher: { "@type": "Organization", name: "Lux Veritae Skin Spa" },
      mainEntityOfPage: window.location.href,
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [a]);
  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Lux Veritae Journal`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", post.dek || post.title);
  }, [post]);
  if (!a && !done) return <main className="min-h-[60vh] bg-white" data-testid="article-loading" />;
  if (!a) return <Navigate to="/blog" replace />;
  const more = ARTICLES.filter((x) => x.slug !== slug).slice(0, 4);
  const [first, rest] = splitBody(a.body);
  const shareUrl = a.source === "ai" ? `${process.env.REACT_APP_BACKEND_URL}/api/share/blog/${a.slug}` : undefined;
  return (
    <main className="overflow-x-clip bg-white" data-testid="article-page">
      <header className="mx-auto max-w-4xl px-5 pb-12 pt-16 text-center md:pt-24">
        <Link to="/blog" className="text-[10px] uppercase tracking-[0.45em] text-neutral-500 hover:text-neutral-950" data-testid="article-journal-link">The Journal</Link>
        <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-950">{a.category}</p>
        <h1 className="font-vogue mt-5 break-words text-4xl leading-[1.04] text-neutral-950 sm:text-5xl lg:text-6xl" data-testid="article-title">{a.title}</h1>
        <p className="font-display mx-auto mt-6 max-w-2xl text-2xl font-light italic leading-snug text-neutral-600">{a.dek}</p>
        <p className="mt-8 text-[10px] uppercase tracking-[0.28em] text-neutral-500">By {a.author} · {a.date} · {a.read}</p>
        <div className="mt-8"><ShareButtons title={a.title} testid="share-top" shareUrl={shareUrl} /></div>
      </header>
      {a.image && <div className="mx-auto max-w-[1200px] px-4 md:px-8"><img src={a.image} alt={a.title} className="aspect-[16/9] w-full object-cover" /></div>}
      <article className="article-body mx-auto max-w-[680px] px-5 py-16 text-[17px] font-light leading-[1.85] text-neutral-800 md:py-20 [&>p]:my-6" data-testid="article-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{first}</ReactMarkdown>
        <ArticleBookCard article={a} />
        {rest && <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{rest}</ReactMarkdown>}
        <SkinTipsSignup source={a.slug} />
        <div className="mt-14 border-t border-neutral-200 pt-10"><p className="font-display mb-6 text-center text-2xl italic text-neutral-700">Know someone who'd love this?</p><ShareButtons title={a.title} testid="share-bottom" shareUrl={shareUrl} /></div>
      </article>
      <section className="bg-neutral-950 px-5 py-20 text-center text-white">
        <p className="font-display text-xl italic text-neutral-300">Confident enough to leave the lights on.</p>
        <h2 className="font-vogue mt-4 text-4xl md:text-5xl">Your skin, personally composed.</h2>
        <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="mt-10 inline-block bg-white px-8 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-950 hover:bg-neutral-200" data-testid="article-book-btn">Book a Consultation</a>
      </section>
      {more.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-20 md:px-8">
          <p className="mb-12 text-center text-[10px] uppercase tracking-[0.45em] text-neutral-500">More from the Journal</p>
          <div className="mx-auto grid max-w-4xl gap-14 md:grid-cols-2">{more.map((m) => <JournalCard key={m.slug} a={m} />)}</div>
        </section>
      )}
    </main>
  );
}
